import {RRCalendarDayType} from "../../types/RRCalendarDayType";
import {Alert, Badge, Col, Form, FormCheck, FormControl, ListGroup, Modal, Row, Spinner} from "react-bootstrap";
import {useEffect, useState} from "react";
import {CancelIconButton, SaveIconButton} from "../../components/IconButtons";
import {
    checkOverlapTimes,
    dateTimeToString,
    dateToString, dateWithZeroTime, removeEmptyFields, showErrorsListInToast, showMsgInToast,
    timeDiffInMinutes,
    timeDiffToString,
    timeToString,
    zeroToEmptyString
} from "../../tools";
import {RREventType} from "../../types/RREventType";
import ReactDatePicker from "react-datepicker";
import {MaybeDate} from "../../types/Maybe";
import {collection, FirestoreError, getDocs, query, updateDoc, where} from "firebase/firestore";
import NumberInputField from "../../components/inputs/NumberInputField";
import EventFaceIcon from "../../views/calendar/EventFaceIcon";
import {useAppSelector} from "../../hooks";
import {selectCalendarData, selectCurrentEmployee, setReportCalendarEvent} from "../../../features/AppStateSlice";
import {appFirestore} from "../../../index";
import {calculateWorkhours} from "../../../firebase/FirebaseEmployeeStatisticsLoader";
import {useWarehouseLocks, WarehouseLockNotice} from "../../warehouse/useWarehouseLocks";
import {updateCalendarItemsWithChangeLog} from "../../views/calendar/change-log/calendarChangeLog";

type MCalendarItemReportProps = {
    event: RREventType;
    items: RRCalendarDayType[];
    isSupervisor: boolean;
    readonly: boolean;
    onDone: () => void;
}

const MCalendarItemReport: React.FC<MCalendarItemReportProps> = ({event, items, readonly: originalReadonly, isSupervisor, onDone}) => {
    const item = items[0]!;
    const currentEmployee = useAppSelector(selectCurrentEmployee);
    const [endTime, setEndTime] = useState<Date>(item.eventDayEnd || item.eventDayStart || new Date());
    const [notes, setNotes] = useState<string>(item.notes || "");
    const [face, setFace] = useState<string>(item.face || "NOT SET");
    const [restTime, setRestTime] = useState(item.restTime || 0);
    const [bonus, setBonus] = useState(item.bonus || 0);
    const [working, setWorking] = useState<boolean>(false);
    const [isConfirmed, setIsConfirmed] = useState(item.isConfirmed);
    const [errors, setErrors] = useState<string[]>([]);
    const calendarData = useAppSelector(selectCalendarData);
    const warehouseStatus = useWarehouseLocks();
    const lockAccess = items.map(value => warehouseStatus(value.eventDayStart, value.eventId, value.department))
        .find(access => access.blocked) || warehouseStatus(item.eventDayStart, item.eventId, item.department);
    const readonly = originalReadonly || lockAccess.blocked;

    function updateEndTime(e: Date | null) {
        if (!e || e < item.eventDayStart!) {
            setErrors(["Крайният час не може да бъде преди началния!"]);
            return;
        }
        setEndTime(e);
        const diffInHours = timeDiffInMinutes(item.eventDayStart!, e, 0) / 60;
        if (diffInHours > 24) {
            setErrors(["Внимание! Отчитате повече от 24часа. Моля, проверете крайния час!"]);
        } else {
            setErrors([]);
        }
    }

    function validateData(data: {eventDayEnd?: Date | null}) {
        if(readonly)  {
            return false;
        }
        if(data.eventDayEnd && item.eventDayStart && data.eventDayEnd < item.eventDayStart) {
            setErrors(["Внимание! Крайният час е преди началния!"]);
            return false;
        }

        let eventFoundName: string = "";
        let calendarItemFound: RRCalendarDayType | undefined = undefined;
        if(item.eventId !== "WAREHOUSE") {
            calendarData.forEach(
                value => {
                    if (calendarItemFound) {
                        return;
                    }

                    calendarItemFound = value.items.find(
                        item => {
                            return item.employeeId === item.employeeId &&
                                item.id !== item.id &&
                                item.eventId !== "WAREHOUSE" &&
                                item.eventDayEnd &&
                                data.eventDayEnd &&
                                checkOverlapTimes(item.eventDayStart, item.eventDayEnd, item.eventDayStart, data.eventDayEnd)
                        }
                    );
                    if (calendarItemFound) {
                        eventFoundName = value.events.find(e => e.id === calendarItemFound?.eventId)?.eventName || "";
                    }
                }
            );
        }

        if (calendarItemFound) {
            setErrors([`Внимание! Служителят е зает на друго събитие: ${eventFoundName}!`]);
            return false;
        }
        return true;
    }

    async function onSaveReport() {
        if (readonly || working) return;
        try {
            setWorking(true);
            const data = {
                eventDayEnd: endTime,
                restTime: restTime || 0,
                bonus: bonus || 0,
                isConfirmed: isSupervisor ? isConfirmed || false : false,
                notes: isSupervisor ? notes || "" : undefined,
                face: isSupervisor ? (face || "NOT SET") : undefined,
            }

            if (item.ref) {
                const _data = removeEmptyFields(data);

                if (!_data || !validateData(_data)) {
                    return;
                }


                if (_data) {
                    if (items.length) {
                        const selectedEmployees = items.map(item => item.employeeId);
                        const eventStart = dateWithZeroTime(item.eventDayStart)!;
                        const eventEnd = new Date(eventStart);
                        eventEnd.setDate(eventEnd.getDate() + 1);
                        const calndarEvents = await getDocs(query(collection(appFirestore, "calendar"),
                            where("eventId", "==", item.eventId),
                            where("eventDayStart", ">=", eventStart),
                            where("eventDayStart", "<", eventEnd)));

                        for (const employeeId of selectedEmployees) {
                                const emplEvent = calndarEvents.docs.find(d => d.get("employeeId") === employeeId &&
                                    (item.eventId !== "WAREHOUSE" || (d.get("department") || "montage") === item.department));
                                if (!emplEvent) throw new Error("Не е намерен запис за избран служител в този отдел.");
                                if (emplEvent) {
                                    const newData = {
                                        ...emplEvent.data(),
                                        employeeId: employeeId,
                                        eventDayEnd: _data.eventDayEnd,
                                        restTime: _data.restTime || 0,
                                        bonus: _data.bonus || 0,
                                        isConfirmed: _data.isConfirmed || false,
                                        notes: _data.notes || "",
                                        face: _data.face || "",
                                    };
                                    await updateCalendarItemsWithChangeLog([{
                                        ref: emplEvent.ref,
                                        data: newData,
                                        context: {eventNumber: event.eventNumber, eventName: event.eventName},
                                    }], currentEmployee);
                                }
                        }
                        if (endTime) {
                            // await calculateWorkhours(endTime, [...selectedEmployees])
                        }
                    } else {
                        await updateCalendarItemsWithChangeLog([{
                            ref: item.ref,
                            data: _data,
                            context: {eventNumber: event.eventNumber, eventName: event.eventName},
                        }], currentEmployee);
                        if (endTime) {
                            // await calculateWorkhours(endTime, [item.employeeId])
                        }
                    }


                    if (event.id !== "WAREHOUSE") {
                        let isDone = true;
                        const items = await getDocs(query(collection(appFirestore, "calendar"),
                            where("eventId", "==", event.id),
                            where("dayType", "==", "WORKDAY")
                        ));

                        const isNew = items.empty;
                        items.forEach(
                            i => {
                                if (!i.get("eventDayEnd") || i.get("isConfirmed") !== true) {
                                    isDone = false;
                                }
                            }
                        )
                        if (isNew) {
                            if (event.eventStatus !== "НОВО") {
                                await updateDoc(event.ref!, {eventStatus: "ПОТВЪРДЕНО"});
                            }
                        } else {
                            if (isDone && event.eventStatus !== "ЗАВЪРШЕНО") {
                                await updateDoc(event.ref!, {eventStatus: "ЗАВЪРШЕНО"});
                            }
                        }
                    }
                }
                onDone();
                showMsgInToast("Данните са запазени успешно!");
            }
        } catch
            (e) {
            showErrorsListInToast("Възникна проблем при запазването на данните:",
                [e instanceof Error ? e.message : "Неуспешен запис.", "При групова операция проверете кои записи са запазени."])
        } finally {
            setWorking(false);
        }
    }

    return (
        <Modal show={true}>
            <Modal.Body>
                <WarehouseLockNotice access={lockAccess}/>
                {
                    items.map(
                        item =>
                            <Row className={"fs-4 fw-bold"} key={item.id}>
                                <Col>{item.employeeName}</Col>
                                <Col className={"text-end"} xs={"auto"}>{item.isSupervisor &&
                                    <Badge pill bg="primary">S</Badge>}</Col>
                            </Row>
                    )
                }
                <Row className={"small text-primary mt-3 pt-3 border-0 border-top"}>
                    <Col>
                        <div><b>{event.eventName}</b></div>
                        <div className={"mt-2"}>
                            {
                                [event.place, event.clientName, event.clientPhone].filter(
                                    item => item !== "" && item !== event.eventName
                                ).join(", ")
                            }
                        </div>
                    </Col>
                    <Col xs={"auto"}>
                        <i>От: {dateToString(event.firstDate)}</i>
                        {
                            event.lastDate && <><br/><i>До: {dateToString(event.lastDate)}</i></>
                        }
                    </Col>
                </Row>


                <Row className={"mt-3 justify-content-between"}>
                    <Col xs={"6"}>
                        <Row className={""}>
                            <Col xs={"12"} className={"lh-2-5em me-0 pe-0"}><b>Краен час:</b></Col>
                            <Col xs={"12"}>
                                <ReactDatePicker onChange={date => updateEndTime(date || new Date())}
                                                 disabled={readonly}
                                                 required name={"endTime"}
                                                 dateFormat="dd.MM.yyyy HH:mm"
                                                 selected={endTime}
                                                 className={"form-control"}
                                                 showTimeSelect
                                                 timeCaption={"Край:"}
                                                 timeIntervals={30}
                                                 value={dateTimeToString(endTime)}/>
                            </Col>
                        </Row>
                    </Col>
                    <Col xs={6}>
                        <Row className={""}>
                            <Col xs={"12"} className={"lh-2-5em me-0 pe-0"}><b>Отработени часове:</b></Col>
                            <Col xs={"12"}>
                                {
                                    endTime ? <span className={"text-primary fs-4 mt-3 fw-bold ps-1"}>{
                                        timeDiffToString(item.eventDayStart!, endTime, restTime)
                                    }</span> : "-"
                                }
                            </Col>
                        </Row>
                    </Col>
                    <Col xs={"12"}>
                        <Row>
                            <Col xs={"6"}>
                                <NumberInputField
                                    label={<b>Почивка:</b>}
                                    disabled={readonly}
                                    fieldClass={"w-100px ms-3"}
                                    value={restTime}
                                    formatter={value => zeroToEmptyString(value)}
                                    onChange={newValue => setRestTime(newValue)}
                                />
                            </Col>
                            <Col xs={"6"}>
                                <NumberInputField label={<b>Бонус:</b>}
                                                  disabled={readonly}
                                                  fieldClass={"w-100px ms-3"}
                                                  value={bonus}
                                                  formatter={value => zeroToEmptyString(value)}
                                                  onChange={currentEmployee?.isSuperAdmin ? newValue => setBonus(newValue) : undefined}
                                />
                            </Col>
                        </Row>
                    </Col>
                    <Col xs={"12"}>
                        <Row>
                            <Col xs={"12"} className={"lh-2-5em me-0 pe-0"}>
                                <b>Оценка:</b>
                            </Col>
                            <Col xs={"12"}>
                                {
                                    isSupervisor && readonly === false ?
                                        <ListGroup horizontal
                                                   activeKey={face === "NOT SET" ? undefined : face}
                                                   onSelect={eventKey => setFace(eventKey || "GOOD")}
                                        >
                                            <ListGroup.Item eventKey={"GOOD"} disabled={readonly}>
                                                <EventFaceIcon face={"GOOD"}/>
                                            </ListGroup.Item>
                                            <ListGroup.Item eventKey={"NEUTRAL"}
                                                            disabled={readonly}>
                                                <EventFaceIcon face={"NEUTRAL"}/>
                                            </ListGroup.Item>
                                            <ListGroup.Item eventKey={"BAD"} disabled={readonly}>
                                                <EventFaceIcon face={"BAD"}/>
                                            </ListGroup.Item>
                                        </ListGroup> :
                                        <div>
                                            <EventFaceIcon face={item?.face || ""} withColor={true}/></div>
                                }
                            </Col>
                        </Row>
                    </Col>
                </Row>


                <Row>
                    <Col xs={"12"} className={"mt-3"}>
                        <b>Бележки:</b>
                    </Col>
                    <Col xs={"12"}>
                        <FormControl as={"textarea"} disabled={readonly} value={notes}
                                     onChange={e => setNotes(e.target.value)}/>
                    </Col>
                </Row>
            </Modal.Body>
            <Modal.Footer className={"ps-0 pe-0 pb-3"}>
                {
                    working ?
                        <Row>
                            <Col className={"text-center"}>
                                <Spinner animation={"border"}/>
                            </Col>
                        </Row>
                        :
                        <>
                            <Row className={"mt-2 w-100"}>
                                <Col xs={"12"}>
                                    {
                                        errors.length > 0 &&
                                        <Alert variant={"danger"}
                                               className={"text-start w-100"}
                                        >
                                            <ul className={"mb-0"}>
                                                {
                                                    errors.map((error, index) => <li key={index}>{error}</li>)
                                                }
                                            </ul>
                                        </Alert>
                                    }
                                </Col>
                            </Row>

                            <Row>
                                <Col xs={"auto"}>
                                    <Form.Check checked={isConfirmed} id={"isConfirmed"}
                                                label={<p className={"pt-1"}><b>&nbsp;Потвърдено от супервайзор</b></p>}
                                                onChange={event => setIsConfirmed(event.target.checked)}
                                                disabled={readonly}
                                    />
                                </Col>
                                <Col/>
                                {
                                    !readonly &&
                                    <Col>
                                        <SaveIconButton onClick={onSaveReport}/>
                                    </Col>
                                }
                                <Col>
                                    <CancelIconButton onClick={() => onDone()}/>
                                </Col>
                            </Row>
                        </>
                }
            </Modal.Footer>
        </Modal>
    )
}

export default MCalendarItemReport;
